/**
 * Beu admin: the Support desk (approved mockup "Beu Support desk, admin").
 *
 * Where the Beu team answers what Bailey couldn't: a queue of every salon's
 * tickets (urgent first, then the longest waiting), the ticket with Bailey's
 * summary and the conversation, Bailey's draft ready to send, and the salon
 * beside it with what was sent with the message and their earlier tickets.
 *
 * Stand-in data (supportDeskData.js) until the backend's support endpoints
 * exist (docs/MESSAGES_BACKEND.md, section 21). Styles are namespaced asd-.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminWrapper from '../AdminWrapper';
import {
    loadDesk, saveDesk, resetDesk, queueOf, waitLabel, medianFirstReply, deskActions, PRIORITY,
} from './supportDeskData';
import '../Admin.css';
import './AdminSupport.css';

const VIEWS = [
    { id: 'open', label: 'Needs a person' },
    { id: 'mine', label: 'Mine' },
    { id: 'fixed', label: 'Fixed' },
    { id: 'bailey', label: 'Solved by Bailey' },
];

const two = (n) => (n < 10 ? `0${n}` : String(n));
const clock = (iso) => { const d = new Date(iso); return `${two(d.getHours())}:${two(d.getMinutes())}`; };

/** The window is narrow enough to stack the desk (one pane at a time). */
function useNarrow(max) {
    const read = () => window.innerWidth < max;
    const [narrow, setNarrow] = useState(read);
    useEffect(() => {
        const on = () => setNarrow(read());
        window.addEventListener('resize', on);
        return () => window.removeEventListener('resize', on);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    return narrow;
}

function SalonMark({ salon, size = 36 }) {
    return <span className="asd-mark" style={{ width: size, height: size, background: salon.colour, fontSize: Math.round(size * 0.34) }} aria-hidden="true">{salon.mark}</span>;
}

function Stats({ desk }) {
    const open = desk.tickets.filter((t) => t.status !== 'fixed');
    const urgent = open.filter((t) => t.priority === 'urgent').length;
    const median = medianFirstReply(desk);
    const pct = desk.askedToday ? Math.round((desk.solvedByBaileyToday / desk.askedToday) * 100) : 0;
    return (
        <div className="asd-stats">
            <div className="asd-stat"><small>Solved by Bailey today</small><b>{desk.solvedByBaileyToday} of {desk.askedToday}</b><span>{pct}%</span></div>
            <div className="asd-stat"><small>Waiting for a person</small><b>{open.length}</b><span className={urgent ? 'is-urgent' : ''}>{urgent ? `${urgent} urgent` : 'None urgent'}</span></div>
            <div className="asd-stat"><small>First reply from the team</small><b>{median == null ? 'None yet' : `${median} min`}</b><span>median</span></div>
        </div>
    );
}

function Queue({ desk, view, onView, activeRef, onOpen }) {
    const list = view === 'bailey' ? [] : queueOf(desk, view);
    const counts = { open: queueOf(desk, 'open').length, mine: queueOf(desk, 'mine').length };
    const owner = (id) => (id === desk.me.id ? 'You' : (desk.team.find((p) => p.id === id) || {}).name || 'Unassigned');
    return (
        <aside className="asd-queue" aria-label="Tickets">
            <div className="asd-views" role="tablist">
                {VIEWS.map((v) => (
                    <button key={v.id} type="button" role="tab" aria-selected={view === v.id} className={view === v.id ? 'is-on' : ''} onClick={() => onView(v.id)}>
                        {v.label}{counts[v.id] ? <span>{counts[v.id]}</span> : null}
                    </button>
                ))}
            </div>
            <div className="asd-list" role="list">
                {view === 'bailey' ? desk.solved.map((s) => (
                    <div key={s.ref} role="listitem" className="asd-row is-static">
                        <span className="asd-dot is-solved" aria-hidden="true" />
                        <span className="asd-row-body"><b>{s.title}</b><small>{s.salon} · {clock(s.at)}</small></span>
                    </div>
                )) : list.map((t) => (
                    <button key={t.ref} type="button" role="listitem" className={`asd-row${t.ref === activeRef ? ' is-active' : ''}`} onClick={() => onOpen(t.ref)}>
                        <span className={`asd-dot is-${t.status === 'fixed' ? 'fixed' : t.priority}`} aria-label={t.status === 'fixed' ? 'Fixed' : PRIORITY[t.priority]} />
                        <span className="asd-row-body">
                            <b>{t.title}</b>
                            <small>{t.salon.name} · {owner(t.ownerId)}</small>
                        </span>
                        <span className={`asd-wait${t.priority === 'urgent' && t.status !== 'fixed' ? ' is-urgent' : ''}`}>{t.status === 'fixed' ? 'Fixed' : waitLabel(t.createdAt)}</span>
                    </button>
                ))}
                {view !== 'bailey' && !list.length && <div className="asd-empty">{view === 'mine' ? 'Nothing on your plate' : view === 'fixed' ? 'Nothing fixed yet' : 'Nobody is waiting. Nice work.'}</div>}
            </div>
        </aside>
    );
}

function Thread({ ticket, desk }) {
    const end = useRef(null);
    useEffect(() => { if (end.current) end.current.scrollIntoView({ block: 'end' }); }, [ticket.ref, ticket.messages.length]);
    const name = (m) => (m.from === 'salon' ? `${m.who}, ${ticket.salon.name}` : m.from === 'bailey' ? 'Bailey' : m.who === desk.me.id ? 'You' : (desk.team.find((p) => p.id === m.who) || {}).name || 'Beu team');
    return (
        <div className="asd-thread">
            {ticket.messages.map((m) => (
                <div key={m.id} className={`asd-msg is-${m.from}`}>
                    {m.from === 'note' ? <span className="asd-note-tag">Internal note</span> : null}
                    <p>{m.text}</p>
                    <small>{m.from === 'note' ? (m.who === desk.me.id ? 'You' : name(m)) : name(m)} · {clock(m.at)}</small>
                </div>
            ))}
            <span ref={end} />
        </div>
    );
}

function Reply({ ticket, onSend }) {
    const [text, setText] = useState(ticket.draft || '');
    const [note, setNote] = useState(false);
    const usedDraft = !!ticket.draft && text === ticket.draft;
    useEffect(() => { setText(ticket.draft || ''); setNote(false); }, [ticket.ref]); // eslint-disable-line react-hooks/exhaustive-deps
    const send = (fix) => { const clean = text.trim(); if (!clean) return; onSend(clean, { note, fix: fix && !note }); setText(''); };
    return (
        <div className={`asd-reply${note ? ' is-note' : ''}`}>
            <div className="asd-reply-mode" role="tablist">
                <button type="button" role="tab" aria-selected={!note} className={!note ? 'is-on' : ''} onClick={() => setNote(false)}>Reply to {ticket.salon.owner}</button>
                <button type="button" role="tab" aria-selected={note} className={note ? 'is-on' : ''} onClick={() => setNote(true)}>Internal note</button>
            </div>
            {!note && usedDraft && <div className="asd-draft-tag">Bailey's draft. Check it before you send</div>}
            <textarea rows={4} value={text} placeholder={note ? 'Only the Beu team sees notes' : `Reply to ${ticket.salon.owner} at ${ticket.salon.name}`}
                onChange={(e) => { const v = e.target.value; setText(v); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(false); } }} />
            <div className="asd-reply-actions">
                {note ? (
                    <button type="button" className="asd-btn is-primary" disabled={!text.trim()} onClick={() => send(false)}>Add note</button>
                ) : (
                    <>
                        <button type="button" className="asd-btn" disabled={!text.trim()} onClick={() => send(false)}>Reply</button>
                        <button type="button" className="asd-btn is-primary" disabled={!text.trim()} onClick={() => send(true)}>Reply and mark fixed</button>
                    </>
                )}
            </div>
        </div>
    );
}

function SalonPanel({ ticket, desk }) {
    const earlier = desk.tickets.filter((t) => t.salon.id === ticket.salon.id && t.ref !== ticket.ref);
    return (
        <aside className="asd-salon" aria-label="Salon">
            <div className="asd-salon-head">
                <SalonMark salon={ticket.salon} size={44} />
                <span><b>{ticket.salon.name}</b><small>{ticket.salon.plan} · {ticket.salon.months} months</small></span>
            </div>
            {ticket.context.length > 0 && (
                <section>
                    <h4>Sent with the message</h4>
                    <div className="asd-chips">{ticket.context.map((c) => <span key={c}>{c}</span>)}</div>
                </section>
            )}
            <section>
                <h4>Earlier tickets</h4>
                {earlier.length ? (
                    <ul className="asd-earlier">{earlier.map((t) => <li key={t.ref}><b>{t.title}</b><small>{t.ref} · {t.status === 'fixed' ? 'Fixed' : 'Open'}</small></li>)}</ul>
                ) : <p className="asd-muted">None before this one</p>}
            </section>
            <Link className="asd-btn is-block" to="/beu-admin-x7k9/businesses">Open in Businesses</Link>
        </aside>
    );
}

function TicketView({ ticket, desk, onAct, onBack, narrow, wide }) {
    const ownerName = ticket.ownerId ? (ticket.ownerId === desk.me.id ? 'You' : (desk.team.find((p) => p.id === ticket.ownerId) || {}).name) : null;
    return (
        <>
            <section className="asd-ticket" aria-label={`Ticket ${ticket.ref}`}>
                <header className="asd-ticket-head">
                    {narrow && <button type="button" className="asd-back" onClick={onBack} aria-label="Back to tickets">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>
                    </button>}
                    <div className="asd-ticket-title">
                        <small>{ticket.ref} · Raised by Bailey · {clock(ticket.createdAt)}</small>
                        <h2>{ticket.title}</h2>
                    </div>
                    <span className={`asd-chip is-${ticket.status === 'fixed' ? 'fixed' : ticket.priority}`}>{ticket.status === 'fixed' ? 'Fixed' : PRIORITY[ticket.priority]}</span>
                    {ticket.status === 'fixed'
                        ? <button type="button" className="asd-btn" onClick={() => onAct('reopen')}>Reopen</button>
                        : ownerName ? <span className="asd-owner">{ownerName === 'You' ? 'Yours' : ownerName}</span>
                            : <button type="button" className="asd-btn is-primary" onClick={() => onAct('take')}>Take it</button>}
                </header>
                {ticket.summary && (
                    <div className="asd-summary">
                        <span className="asd-summary-tag">Bailey's summary</span>
                        <p>{ticket.summary}</p>
                    </div>
                )}
                <Thread ticket={ticket} desk={desk} />
                {ticket.status !== 'fixed' ? <Reply ticket={ticket} onSend={(text, opts) => onAct('reply', text, opts)} />
                    : <div className="asd-fixed-line">Fixed{ticket.rated ? `, rated ${ticket.rated === 'good' ? 'All good' : 'Still broken'}` : ''}. The salon was asked "How did we do?"</div>}
            </section>
            {wide && <SalonPanel ticket={ticket} desk={desk} />}
        </>
    );
}

export function SupportDesk() {
    const [desk, setDesk] = useState(loadDesk);
    const [view, setView] = useState('open');
    // Wide: the first waiting ticket is open. Narrow: the list comes first.
    const [activeRef, setActiveRef] = useState(() => (window.innerWidth < 900 ? null : (queueOf(loadDesk(), 'open')[0] || {}).ref || null));
    const narrow = useNarrow(900);
    const wide = !useNarrow(1280);
    const ticket = useMemo(() => desk.tickets.find((t) => t.ref === activeRef) || null, [desk, activeRef]);

    const update = (next) => { setDesk(next); saveDesk(next); };
    const act = (kind, text, opts) => {
        if (!ticket) return;
        if (kind === 'take') update(deskActions.take(desk, ticket.ref));
        if (kind === 'reopen') update(deskActions.reopen(desk, ticket.ref));
        if (kind === 'reply') {
            const next = deskActions.reply(desk, ticket.ref, text, opts);
            update(next);
            // Fixed: straight on to the next one waiting (one at a time on a phone, so back to the list there)
            if (opts && opts.fix) {
                const waiting = queueOf(next, view === 'mine' ? 'mine' : 'open')[0];
                setActiveRef(narrow ? null : (waiting ? waiting.ref : ticket.ref));
            }
        }
    };
    const changeView = (v) => {
        setView(v);
        const first = v === 'bailey' ? null : queueOf(desk, v)[0];
        if (!narrow) setActiveRef(first ? first.ref : null);
    };

    const showQueue = !narrow || !ticket;
    const showTicket = !narrow || !!ticket;

    return (
        <div className="asd">
            <div className="asd-top">
                <div>
                    <h1>Support</h1>
                    <p>Everything Bailey couldn't solve, from every salon. Urgent first, then the longest waiting.</p>
                </div>
                <button type="button" className="asd-link" onClick={() => { const fresh = resetDesk(); setDesk(fresh); setActiveRef((queueOf(fresh, 'open')[0] || {}).ref || null); setView('open'); }}>Reset demo data</button>
            </div>
            <Stats desk={desk} />
            <div className={`asd-desk${narrow ? ' is-narrow' : ''}`}>
                {showQueue && <Queue desk={desk} view={view} onView={changeView} activeRef={activeRef} onOpen={setActiveRef} />}
                {showTicket && (ticket
                    ? <TicketView ticket={ticket} desk={desk} onAct={act} onBack={() => setActiveRef(null)} narrow={narrow} wide={wide} />
                    : <div className="asd-pick">{view === 'bailey' ? 'Bailey answered these with a guide and the salon said it helped.' : 'Pick a ticket'}</div>)}
            </div>
        </div>
    );
}

export default function AdminSupportDesk() {
    return <AdminWrapper><SupportDesk /></AdminWrapper>;
}
